import { CrudPage } from '@/components/CrudPage';
import { DataTable } from '@/components/DataTable';
import { Badge, Card } from '@/components/ui';

interface SipAccount {
  id: string;
  name: string;
  username: string;
  host: string;
  port: number;
  transport: string;
  register: boolean;
  registration_state: string;
  caller_id_number: string | null;
  max_channels: number;
  enabled: boolean;
}

interface Did {
  id: string;
  number: string;
  label: string | null;
  inbound_bot_id: string | null;
  enabled: boolean;
}

const REGISTRATION_TONES: Record<string, 'green' | 'amber' | 'red' | 'gray'> = {
  REGISTERED: 'green',
  REGISTERING: 'amber',
  FAILED: 'red',
  UNKNOWN: 'gray',
};

export default function Sip() {
  return (
    <>
      <CrudPage<SipAccount>
        title="SIP accounts"
        description="Native calling mode — Glee places and receives calls itself, without Vicidial."
        resource="sip"
        path="/sip-accounts"
        queryKey="sip-accounts"
        searchPlaceholder="Search trunks…"
        emptyTitle="No SIP trunks configured"
        emptyDescription="Only needed for native calling. In Vicidial mode the dialer owns the trunk."
        createLabel="New SIP trunk"
        fields={[
          { name: 'name', label: 'Name', required: true, placeholder: 'Primary trunk' },
          {
            name: 'username',
            label: 'Username',
            required: true,
            immutable: true,
            hint: 'The SIP auth username. It is written into the Asterisk endpoint.',
          },
          {
            name: 'password',
            label: 'Password',
            type: 'password',
            hint: 'Stored encrypted and never shown again.',
          },
          { name: 'host', label: 'Host', required: true, placeholder: 'sip.provider.example' },
          { name: 'port', label: 'Port', type: 'number', defaultValue: 5060 },
          {
            name: 'transport',
            label: 'Transport',
            type: 'select',
            defaultValue: 'udp',
            options: ['udp', 'tcp', 'tls', 'ws', 'wss'].map((value) => ({
              value,
              label: value.toUpperCase(),
            })),
          },
          { name: 'caller_id_number', label: 'Caller ID number' },
          { name: 'caller_id_name', label: 'Caller ID name' },
          {
            name: 'max_channels',
            label: 'Max concurrent channels',
            type: 'number',
            defaultValue: 30,
          },
        ]}
        columns={[
          {
            key: 'name',
            header: 'Trunk',
            render: (account) => (
              <div>
                <div className="font-medium text-ink-900 dark:text-ink-50">{account.name}</div>
                <div className="font-mono text-xs text-ink-500">
                  {account.username}@{account.host}:{account.port}
                </div>
              </div>
            ),
          },
          {
            key: 'transport',
            header: 'Transport',
            render: (account) => (
              <Badge tone="gray">{account.transport.toUpperCase()}</Badge>
            ),
          },
          {
            key: 'registration',
            header: 'Registration',
            render: (account) =>
              account.register ? (
                <Badge tone={REGISTRATION_TONES[account.registration_state] ?? 'gray'}>
                  {account.registration_state}
                </Badge>
              ) : (
                <span className="text-xs text-ink-400">No registration</span>
              ),
          },
          {
            key: 'caller_id',
            header: 'Caller ID',
            render: (account) => account.caller_id_number ?? <span className="text-ink-400">—</span>,
          },
          {
            key: 'channels',
            header: 'Max channels',
            numeric: true,
            render: (account) => account.max_channels,
          },
          {
            key: 'enabled',
            header: 'Status',
            render: (account) => (
              <Badge tone={account.enabled ? 'green' : 'gray'}>
                {account.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            ),
          },
        ]}
      >
        <Card className="mb-4">
          <p className="text-sm text-ink-600 dark:text-ink-300">
            <strong className="text-ink-900 dark:text-ink-50">Two calling modes.</strong> In
            Vicidial mode the dialer places every call and Glee only takes over once the
            customer answers — the platform never dials. Native mode uses the trunks below so
            Glee can place and receive calls on its own.
          </p>
        </Card>
      </CrudPage>

      <div className="mx-auto mt-6 max-w-7xl">
        <DataTable<Did>
          title="Inbound numbers (DIDs)"
          description="Route an inbound number straight to a bot."
          queryKey="dids"
          path="/dids"
          searchPlaceholder="Search numbers…"
          emptyTitle="No DIDs configured"
          emptyDescription="Add a DID to answer inbound calls with a Glee AI Bot."
          columns={[
            {
              key: 'number',
              header: 'Number',
              render: (did) => <span className="font-mono text-sm">{did.number}</span>,
            },
            {
              key: 'label',
              header: 'Label',
              render: (did) => did.label ?? <span className="text-ink-400">—</span>,
            },
            {
              key: 'bot',
              header: 'Inbound bot',
              render: (did) =>
                did.inbound_bot_id ? (
                  <Badge tone="blue">Routed</Badge>
                ) : (
                  <Badge tone="amber">No bot</Badge>
                ),
            },
            {
              key: 'enabled',
              header: 'Status',
              render: (did) => (
                <Badge tone={did.enabled ? 'green' : 'gray'}>
                  {did.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              ),
            },
          ]}
        />
      </div>
    </>
  );
}
